import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ActivityIndicator, Text } from 'react-native'

import { i18n } from '@localization'
import { useNavigation, useRoute } from '@react-navigation/native'
import {
  useAddCustomerNextStateWeb,
  useAgentDetailsWF,
  useGetAgentBanners,
} from '@zet/customer'
import AddCustomerHeader from '@zet/customer/src/components/addCustomerHeader'
import { Button } from '@zet/design-system/button'
import { Checkbox } from '@zet/design-system/checkbox'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B2, H5, SH2, SH3 } from '@zet/design-system/typography'
import {
  experianConsent,
  privacyPolicyForExperianLink,
  privacyPolicyLink,
  termsAndConditionsForExperianLink,
  termsAndConditionsLink,
} from '@zet/feature-brands/utils/constants'
import { styled } from 'nativewind'

import KeyBoardWrapper from '@components/keyboardWrapper'
import OtpInput from '@components/otpInput'

import CustomImageHeader from '../../components/bannerHeader'
import ScreenViewEventWrapper from '../../components/screenViewEventWrapper'

const BoldText = styled(Text, 'font-[Graphie-Bold] text-sm leading-5')

const VerifyOtpAfterShare = () => {
  const { data, isLoading: agentDetailsLoading } = useAgentDetailsWF()
  const { data: bannerData, isLoading: isBannerLoading } = useGetAgentBanners({
    onecode: data?.onecode,
  })
  const [isConsentGiven, setIsConsentGiven] = useState<boolean>(false)
  const route = useRoute()

  const isExperianEnabled = route?.params?.isExperianEnabled

  const [value, setValue] = useState({})
  const [timer, setTimer] = useState<number>(30)

  const { mutate: mutateWithExperiment, isLoading } =
    useAddCustomerNextStateWeb()

  const handleSubmit = () => {
    const pathName = window?.location?.pathname

    const requestBodyData = {
      customerId: data?.customer?.customerId,
      otp: Object.values(value).join(''),
      phoneNumber: data?.customer?.phoneNumber,
      screenName: 'SHOWN_OTP_SCREEN',
      isReconsentFlow: pathName.includes('bureauReportConsent'),
      isVerifyOtpOnly: true,
    }

    mutateWithExperiment(requestBodyData)
  }

  const handleButtonDisablity = () => {
    if (Object.values(value).join('').length === 4) return false
    else return true
  }

  const getOtp = useCallback(() => {
    const pathName = window?.location?.pathname
    const requestBodyData = {
      fullName: data?.customer?.fullName,
      phoneNumber: Number(data?.customer?.phoneNumber),
      screenName: 'SHOWN_PHONE_NUMBER_SCREEN',
      isReconsentFlow: pathName.includes('bureauReportConsent'),
      isVerifyOtpOnly: true,
    }

    if (data?.customer?.phoneNumber) {
      mutateWithExperiment(requestBodyData)
    }
  }, [data?.fullName, mutateWithExperiment, data?.customer?.phoneNumber])

  const navigation = useNavigation()

  useEffect(() => {
    navigation.setOptions({
      header: () => (!bannerData?.imageUrl ? <AddCustomerHeader /> : <></>),
    })
  }, [bannerData?.imageUrl])

  useEffect(() => {
    getOtp()
  }, [getOtp])

  const handlePress = () => {
    prev = 0
    diff = 0
    timerFunction()
    getOtp()
  }

  const interval = 1000
  const timerRef = useRef(0)
  let prev = 0
  let diff = 0
  const timerCallback = () => {
    const date = new Date()
    setTimer((i) => {
      const current = date.getSeconds()
      if (diff === 0) {
        diff = 1
        prev = date.getSeconds()
        return 30
      }

      if (prev > current) diff = 60 - prev + current
      else diff = current - prev

      prev = date.getSeconds()

      if (i > 1)
        if (i + diff < 1) {
          clearInterval(timerRef.current)
          return 1
        } else {
          return i - diff
        }
      else {
        clearInterval(timerRef.current)
        return 0
      }
    })
  }
  const timerFunction = () => {
    timerRef.current = setInterval(timerCallback, interval)
  }
  const otpTimer = useCallback(timerFunction, [timerRef])
  function openTermsAndConditions() {
    window.open(
      isExperianEnabled
        ? termsAndConditionsForExperianLink
        : termsAndConditionsLink
    )
  }

  function openPrivacyPolicy() {
    window.open(
      isExperianEnabled ? privacyPolicyForExperianLink : privacyPolicyLink
    )
  }
  function openExperianTermsAndConditions() {
    window.open(experianConsent)
  }

  useEffect(() => {
    otpTimer()

    return () => {
      clearInterval(timerRef.current)
    }
  }, [])

  if (agentDetailsLoading || isBannerLoading) {
    return (
      <Stack className="flex-1 items-center justify-center bg-white">
        <ActivityIndicator size={'large'} color={'blue'} />
      </Stack>
    )
  }

  return (
    <ScreenViewEventWrapper screenName="verify_otp_after_share">
      <KeyBoardWrapper
        className="flex-1 bg-white"
        BottomSheet={
          <>
            {isExperianEnabled ? (
              <XStack className="mx-4 items-center">
                <Stack className="mr-3">
                  <Checkbox
                    accesibilityLabel="consentToTnc"
                    checked={isConsentGiven}
                    onChange={setIsConsentGiven}
                  />
                </Stack>
                <>
                  <B2>
                    {i18n.t('webFlow.otpScreen.iAgree')}
                    <Pressable
                      onPress={openPrivacyPolicy}
                      className=" ml-1 justify-center"
                      eventName="click_action"
                      eventPayload={{
                        item_name: 'add_customer_privacy_policy',
                      }}
                    >
                      <BoldText>
                        {i18n.t('verifyPhNum.privacyPolicy')}{' '}
                      </BoldText>
                    </Pressable>
                    {i18n.t('webFlow.otpScreen.and')}
                    <Pressable
                      onPress={openTermsAndConditions}
                      className=" ml-1 justify-center"
                      eventName="click_action"
                      eventPayload={{
                        item_name: 'add_customer_terms_of_use',
                      }}
                    >
                      <BoldText>{i18n.t('webFlow.otpScreen.terms')}</BoldText>
                    </Pressable>{' '}
                    {i18n.t('webFlow.otpScreen.zet')}
                    <Pressable
                      onPress={openExperianTermsAndConditions}
                      className=" ml-1 justify-center"
                      eventName="click_action"
                      eventPayload={{
                        item_name: 'add_customer_experian_terms_and_conditions',
                      }}
                    >
                      <BoldText>{i18n.t('webFlow.otpScreen.t&c')}</BoldText>
                    </Pressable>{' '}
                    {i18n.t('webFlow.otpScreen.experian')}
                  </B2>
                </>
              </XStack>
            ) : (
              <Stack className="ml-3">
                <B2>
                  {i18n.t('webFlow.verifyOtpAfterShare.byClicking')}{' '}
                  <XStack>
                    <Pressable
                      onPress={openTermsAndConditions}
                      className=" justify-center "
                    >
                      <SH2 className="font-[Graphie-Bold]">
                        {i18n.t('webFlow.verifyOtpAfterShare.terms')}
                      </SH2>
                    </Pressable>
                    <B2> {i18n.t('webFlow.verifyOtpAfterShare.and')} </B2>

                    <Pressable onPress={openPrivacyPolicy}>
                      <SH2 className="font-[Graphie-Bold]">
                        {i18n.t('webFlow.verifyOtpAfterShare.privacy')}
                      </SH2>
                    </Pressable>
                  </XStack>
                </B2>
              </Stack>
            )}
            <Stack className="mt-4/2 border-grays-30 w-full border-t px-3 py-2">
              <Button
                size="lg"
                fullWidth
                onPress={() => {
                  handleSubmit()
                }}
                disabled={handleButtonDisablity()}
                loading={isLoading}
                eventName="click_action"
                eventPayload={{
                  item_name:
                    'brands_list_recommendation_reconsent_web_give_permission',
                }}
              >
                {i18n.t('webFlow.verifyOtpAfterShare.givePermission')}
              </Button>
            </Stack>
          </>
        }
      >
        <>
          {bannerData?.imageUrl && (
            <CustomImageHeader bannerUrl={bannerData?.imageUrl} />
          )}
          <Stack className="-mt-3 rounded-t-xl bg-white">
            <Stack className=" mt-4/2 w-full px-3 pt-2">
              {bannerData?.imageUrl && (
                <H5 className="text-grays-80 mx-3 font-[Graphie-Bold]">
                  Verify Your Phone Number
                </H5>
              )}
              <SH3
                className={`text-grays-60 mx-3 mt-2 ${
                  !bannerData?.imageUrl && 'text-center'
                }`}
              >
                {i18n.t('webFlow.verifyOtpAfterShare.weHave', {
                  number: data?.customer?.phoneNumber,
                })}
                {`\n`}
                {i18n.t('webFlow.verifyOtpAfterShare.please')}
              </SH3>
            </Stack>
            <Stack>
              <OtpInput value={value} setValue={setValue} otpCount={4} />
            </Stack>
            <Stack className="mt-4 items-center">
              {timer === 0 ? (
                <Pressable onPress={handlePress}>
                  <SH3 className=" text-primary-100">
                    {i18n.t('webFlow.verifyOtpAfterShare.resend')}
                  </SH3>
                </Pressable>
              ) : (
                <B2 className=" text-grays-60 text-center">
                  {`Resend OTP in `}
                  <SH3 className="text-primary-100">
                    00:{timer < 10 ? `0${timer}` : timer}
                  </SH3>
                </B2>
              )}
            </Stack>
          </Stack>
        </>
      </KeyBoardWrapper>
    </ScreenViewEventWrapper>
  )
}

export default VerifyOtpAfterShare
